<template>
  <div>
    <!-- 소셜미디어 스트리밍 카드 -->
    <v-card class="rounded-xl elevation-0 mb-5">
      <v-card-title class="d-flex align-center justify-space-between py-4">
        <div class="card_title">
          <h4 class="text-subtitle-1 font-weight-bold">
            {{ $store.getters.t('social.live.card.title') }}
          </h4>
          <span>
            {{ $store.getters.t('social.live.card.description') }}
            <a class="ml-1">{{ $store.getters.t('social.live.card.guide_link') }}</a>
          </span>
        </div>
        
        <div class="d-flex" v-if="canUseSocialLive">
          <TextButton
            :buttonText="$store.getters.t('social.integration.add_channel')"
            @click="goToSocialSettings"
          />
        </div>
      </v-card-title>
      <v-divider />

      <v-card-text>
        <v-alert
          v-if="warningMessage"
          dense
          type="warning"
          class="mb-3"
          border="left"
          colored-border
        >
          <div class="d-flex align-center justify-space-between">
            <span>{{ warningMessage }}</span>
            <v-btn icon small @click="clearInlineWarning">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-alert>

        <div v-if="!canUseSocialLive" class="social-live-locked pa-8 text-center">
          <v-icon size="56" color="grey darken-1" class="mb-4">mdi-lock-outline</v-icon>
          <h3 class="mb-2">
            {{ $store.getters.t('social.live.card.locked.title') }}
          </h3>
          <p class="mb-0">
            {{ $store.getters.t('social.live.card.locked.description') }}
          </p>
        </div>
        <template v-else>
          <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary"
            class="mb-3"
          />
          <div v-else-if="loadError" class="empty-dashed pa-4 text-center grey--text text--darken-1">
            <v-icon class="mdi mdi-information-outline mr-1" small />
            {{ loadError }}
          </div>

          <div v-else-if="!channels.length" class="empty-dashed pa-4 text-center grey--text text--darken-1">
            <v-icon class="mdi mdi-information-outline mr-1" small />
            {{ $store.getters.t('social.integration.no_channels') }}
          </div>
          <v-row v-else dense>
            <v-col
              v-for="ch in channels"
              :key="ch.id"
              cols="12"
            >
              <v-card
                outlined
                class="rounded-lg"
                :style="!ch.connected ? 'border-color:#fb923c;background:#fff7ed;' : null"
              >
                <v-card-text class="social_card_list">
                  <!-- 헤더 -->
                  <div class="d-flex align-center">
                    <!-- 아바타(섬네일) + 플랫폼 배지 + 경고 점 -->
                    <div class="sm-avatar-wrap">
                      <!-- 48x48 원형 섬네일 -->
                      <v-img
                        :src="resolveThumb(ch)"
                        width="30"
                        height="30"
                        class="sm-avatar"
                        contain
                      />

                      <!-- 우하단 플랫폼 배지(작게) -->
                      <v-img
                        :src="platformIconMap[ch.platform]"
                        width="16"
                        height="16"
                        class="platform-badge"
                        contain
                      />

                      <!-- (옵션) 미연결 경고 점: 우상단 -->
                      <div v-if="!ch.connected" class="sm-disconnect-dot"></div>
                    </div>

                    <div class="flex-grow-1 ml-4">
                      <div class="font-weight-bold black--text">{{ ch.name }}</div>
                      <div class="channel-info-block">
                        <div class="channel-info-row">
                          <span class="channel-info-label">
                            {{ translate('social.history.channel_url_label', 'Channel URL') }}:
                          </span>
                          <template v-if="ch.channelUrl">
                            <a
                              :href="channelLinkHref(ch.channelUrl)"
                              class="channel-info-link"
                              target="_blank"
                              rel="noopener"
                            >
                              {{ truncateText(ch.channelUrl, 40) }}
                            </a>
                          </template>
                          <span v-else class="channel-info-placeholder">
                            {{ ch.account ? truncateText(ch.account) : '-' }}
                          </span>
                        </div>
                        <div
                          class="channel-info-row"
                          v-if="shouldShowLiveUrl(ch)"
                        >
                          <span class="channel-info-label">
                            {{ translate('social.history.live_url_label', '라이브 URL') }}:
                          </span>
                          <a
                            :href="liveUrlForChannel(ch)"
                            class="channel-info-link"
                            target="_blank"
                            rel="noopener"
                          >
                            {{ truncateText(liveUrlForChannel(ch), 40) }}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  

                  <!-- 액션 -->
                  <div class="d-flex align-center">

                    <!-- 상태 박스 -->
                  <div
                    class="sm-status mr-2"
                    :style="!ch.connected ? 'border-radius:8px;background:#ffedd5;border:1px solid #fed7aa;' : null"
                  >
                    <!-- 미연결(재연결 필요) -->
                    <template v-if="!ch.connected">
                      <div class="d-flex align-center pa-1">
                        <v-icon small color="#ea580c" class="mr-2">mdi-alert</v-icon>
                        <span class="caption font-weight-bold" style="color:#9a3412;">
                          {{ $store.getters.t('social.integration.status.requires_reconnect') }}
                        </span>
                      </div>
                    </template>

                    <!-- 연결됨: 상태 4종(대기중/신호중/라이브/오류) -->
                    <template v-else>
                      <div class="d-flex align-center justify-space-between">
                        <div class="d-flex align-center">
                          <span :class="[statusMeta(ch.status).dotClass, 'mr-2']"></span>
                          <span class="caption font-weight-bold" :class="statusMeta(ch.status).textClass">
                            {{ statusMeta(ch.status).label }}
                          </span>
                        </div>
                      </div>
                    </template>
                  </div>

                    <!-- 유튜브 -->
                    <template v-if="ch.platform === 'youtube'">
                      <template v-if="!ch.connected">
                        <v-btn depressed width="150px" raised class="flex-grow-1 mr-1" color="#f97316" dark @click="$emit('connect-youtube', ch.id)">
                          <v-icon small class="mr-1" >mdi-refresh</v-icon>{{ $store.getters.t('social.integration.action.reconnect') }}
                        </v-btn>
                      </template>
                      <template v-else>
                        <v-btn
                          depressed
                          color="grey darken-2"
                          dark
                          class="flex-grow-1 mr-1"
                          :loading="isChannelBusy(ch.id)"
                          :disabled="!canControlChannel(ch) || isChannelBusy(ch.id)"
                          @click="onToggleStream(ch)"
                        >
                          {{
                            isChannelLive(ch)
                              ? $store.getters.t('social.live.button.stop_stream')
                              : $store.getters.t('social.live.button.start_stream')
                          }}
                        </v-btn>
                        <v-tooltip top>
                          <template #activator="{ on, attrs }">
                            <IconButton
                              class="ml-2"
                              small
                              v-bind="attrs"
                              v-on="on"
                              :disabled="isChannelBusy(ch.id)"
                              @click="openStreamModal(ch)"
                            />
                          </template>
                          <span>{{ $store.getters.t('social.live.stream_modal.title') }}</span>
                        </v-tooltip>
                      </template>
                    </template>

                    <!-- 인스타그램 -->
                    <template v-else-if="ch.platform === 'instagram'">
                      <template v-if="!ch.connected">
                        <v-btn depressed class="flex-grow-1 mr-1" width="150px"  color="#f97316" dark @click="$emit('connect-instagram', ch.id)">
                          <v-icon small class="mr-1">mdi-refresh</v-icon>{{ $store.getters.t('social.integration.action.reconnect') }}
                        </v-btn>
                      </template>
                      <template v-else>
                        <v-btn depressed class="flex-grow-1 mr-1" width="150px" color="grey darken-2 white--text" @click="$emit('toggle-instagram-stream', ch.id)">
                          {{
                            ch.status === 'live'
                              ? $store.getters.t('social.live.button.stop_stream')
                              : $store.getters.t('social.live.button.start_stream')
                          }}
                        </v-btn>
                      </template>
                    </template>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </v-card-text>
    </v-card>

    <!-- 채널 추가 모달 -->
    <!-- 채널 추가 / 편집 모달 -->
    <AppDialogLarge
      v-if="isChannelModalOpen"
      :value="isChannelModalOpen"
      :persistent="false"
    >
      <template #title>
        <span style="font-weight:bold;font-size:20px;">{{ dialogTitle }}</span>
      </template>

      <template #close>
        <v-btn icon @click="closeChannelDialog()"><v-icon>mdi-close</v-icon></v-btn>
      </template>

      <template #content>
        <div :key="`${modalStep}-${selectedPlatform || 'none'}-${editingMode}`">
          <!-- STEP 1: 플랫폼 선택 (추가 모드에서만 노출) -->
          <div class="platform-selection" v-if="modalStep === 'select' && !editingMode">
            <v-sheet
              v-for="opt in platformOptions"
              :key="opt.value"
              outlined rounded class="platform-item"
              :class="{ 'is-selected': selectedPlatform === opt.value, 'is-disabled': isPlatformDisabled(opt) }"
              :ripple="!isPlatformDisabled(opt)"
              @click="!isPlatformDisabled(opt) && onSelectPlatform(opt.value)"
            >
              <v-chip v-if="isPlatformDisabled(opt)" label color="gray" x-small class="chip_posi">
                {{ $store.getters.t('common.status.preparing') }}
              </v-chip>
              <div class="d-flex align-center">
                <div :class="['platform-icon', opt.iconClass, 'mr-4']">
                  <v-icon color="white">{{ opt.icon }}</v-icon>
                </div>
                <div class="flex-grow-1">
                  <div class="font-weight-bold">
                    {{ translate(opt.labelKey, opt.labelFallback) }}
                  </div>
                </div>
                <v-icon v-if="selectedPlatform === opt.value" color="#006cea">mdi-check-circle</v-icon>
              </div>
            </v-sheet>
          </div>

          <!-- STEP 2: 안내 -->
          <div v-else-if="modalStep === 'youtubeForm'" class="mt-4">
            <p class="grey--text text--darken-1">
              {{ $store.getters.t('social.live.card.integration_hint') }}
            </p>
          </div>

          <!-- STEP 3: 완료 안내 -->
          <div v-else-if="modalStep === 'completed'" class="mt-6 text-center">
            <v-icon color="#007bef" class="mb-2" large>mdi-check-circle</v-icon>
            <h3 class="font-weight-bold mb-2 black--text">
              {{ $store.getters.t('social.live.modal.completed_title') }}
            </h3>
            <div class="grey--text text--darken-1">
              {{ $store.getters.t('social.live.modal.completed_description') }}
            </div>
          </div>
        </div>
      </template>

      <template #actions>
        <!-- 추가 모드/선택 단계 -->
        <template v-if="modalStep === 'select' && !editingMode">
          <TextButtonBlock :buttonText="$store.getters.t('common.action.cancel')" @click="closeChannelDialog()"/>
          <TextButtonBlock
            :buttonText="$store.getters.t('common.action.next')"
            :disabled="selectedPlatform !== 'youtube'"
            :loading="isProceeding"
            @click="goNextFromSelect"
            isPrimary
          />
        </template>

        <!-- 유튜브 폼 단계 (추가/편집 공용) -->
        <template v-else-if="modalStep === 'youtubeForm'">
          <TextButtonBlock
            :buttonText="$store.getters.t('common.action.close')"
            @click="closeChannelDialog()"
            isPrimary
          />
        </template>
      </template>
    </AppDialogLarge>

    <AppDialogLarge
      v-if="isStreamModalOpen"
      :value="isStreamModalOpen"
      :persistent="false"
    >
      <template #title>
        <span style="font-weight:bold;font-size:20px;">
          {{ $store.getters.t('social.live.stream_modal.title') }}
        </span>
      </template>

      <template #close>
        <v-btn icon @click="closeStreamModal"><v-icon>mdi-close</v-icon></v-btn>
      </template>

      <template #content>
        <div v-if="streamFormLoading" class="py-6 text-center grey--text">
          <v-progress-circular indeterminate color="primary" class="mb-2" />
          <div>{{ $store.getters.t('social.live.stream_modal.loading') }}</div>
        </div>
        <div v-else>
          <h4>{{ $store.getters.t('social.live.stream_modal.name_label') }}</h4>
          <AppTextField v-model="streamForm.title" />

          <div class="description mt-3">
            <h4>{{ $store.getters.t('social.live.stream_modal.description_label') }}</h4>
            <v-textarea
              :placeholder="$store.getters.t('social.live.stream_modal.description_placeholder')"
              dense outlined
              v-model="streamForm.description"
              color="black"
              height="80px"
            />
          </div>

          <div class="option_list d-flex mt-3" style="gap:8px;">
            <div class="flex-grow-1">
              <h4>{{ $store.getters.t('social.upload.visibility.scope') }}</h4>
              <AppSelectList
                v-model="streamForm.privacy"
                :items="privacyItems"
                variant="privacy"
                :placeholder="$store.getters.t('social.upload.visibility_placeholder')"
                :item-height="44"
                :show-items-count="5.5"
                :clearable="false"
              />
            </div>
          </div>

          <div class="mt-4">
            <h4>{{ $store.getters.t('social.upload.made_for_kids_label') }}</h4>
            <v-btn-toggle
              v-model="streamForm.madeForKids"
              class="kids-toggle"
              mandatory
            >
              <v-btn :value="false" depressed>
                {{ $store.getters.t('social.upload.made_for_kids_no') }}
              </v-btn>
              <v-btn :value="true" depressed>
                {{ $store.getters.t('social.upload.made_for_kids_yes') }}
              </v-btn>
            </v-btn-toggle>
            <p class="caption grey--text mt-1">
              {{ $store.getters.t('social.live.stream_modal.made_for_kids_hint') }}
            </p>
          </div>

          <v-alert
            v-if="streamFormError"
            type="error"
            dense
            class="mt-3"
          >
            {{ streamFormError }}
          </v-alert>
        </div>
      </template>

      <template #actions>
        <TextButtonBlock :buttonText="$store.getters.t('common.action.cancel')" @click="closeStreamModal" />
        <TextButtonBlock
          :buttonText="$store.getters.t('common.action.save_changes')"
          isPrimary
          :loading="streamFormLoading"
          @click="saveStreamForm"
        />
      </template>
    </AppDialogLarge>

  </div>
</template>

<script>
import { mapState } from 'vuex';
import TextButton from '@/components/ui/button/TextButton.vue';
import TextButtonBlock from '@/components/ui/button/TextButtonBlock.vue';
import AppDialogLarge from '@/components/app/AppDialogLarge.vue';
import IconButton from '@/components/ui/button/IconButton.vue';
import AppSelectList from '@/components/app/AppSelectList.vue';
import AppTextField from '@/components/app/AppTextfield';
import SocialApi from '@/api/social/SocialApi';

const truthyAllowValues = new Set(['1', 'true', 'y', 'yes', 'on', 'enable', 'enabled']);
const falsyAllowValues = new Set(['0', 'false', 'n', 'no', 'off', 'disable', 'disabled']);
const truthyLiveStatusValues = new Set([
  'live',
  'running',
  'streaming',
  'on',
  'active',
  'started',
  'start',
  'publishing',
  'progress',
]);
const falsyLiveStatusValues = new Set([
  'stopped',
  'stop',
  'off',
  'inactive',
  'idle',
  'pending',
  'waiting',
  'ready',
  'paused',
  'prepared',
]);

export default {
  name: 'SocialMediaStreamingCard',
  components: { TextButton, TextButtonBlock, AppDialogLarge, IconButton, AppSelectList, AppTextField },
  props: {
    context: { type: String, default: null },
    liveInfo: { type: Object, default: null },
    sourceType: { type: String, default: null },
    defaultTitle: { type: String, default: '' },
  },
  created() {
    this.resetLiveSessionState();
    if (this.canUseSocialLive) {
      this.loadChannels();
    }
  },
  data() {
    return {
      channels: [],
      loading: false,
      loadError: null,
      actionLoading: {},
      currentSessionId: null,
      sessionDefaultSettings: null,
      platformIconMap: {
        youtube: require('@/styles/youtube.png'),
        instagram: require('@/styles/instgram.png'),
      },
      thumbsMap: {
        thum01: require('@/styles/thum01.png'),
        thum02: require('@/styles/thum02.png'),
        thum03: require('@/styles/thum03.png'),
        thum04: require('@/styles/thum04.png'),
        thum05: require('@/styles/thum05.png'),
      },
      defaultThumb: require('@/styles/socialimage.png'),
      platformOptions: [
        {
          value: 'youtube',
          labelKey: 'social.live.platform.youtube_channel',
          labelFallback: 'YouTube channel',
          icon: 'mdi-youtube',
          iconClass: 'youtube',
        },
        {
          value: 'instagram',
          labelKey: 'social.live.platform.instagram_channel',
          labelFallback: 'Instagram channel',
          icon: 'mdi-instagram',
          iconClass: 'insta',
        },
      ],
      isChannelModalOpen: false,
      selectedPlatform: null,
      isProceeding: false,
      // youtube settings edit modal
      modalStep: 'select', 

      // 편집 모드 관련
      editingMode: false,
      editingChannel: null,

      // 상태 라벨/스타일 매핑
      statusMap: {
        idle:         { labelKey: 'social.live.status.idle',         dotClass: 'status-dot',       textClass: 'grey--text text--darken-2' },
        warming:      { labelKey: 'social.live.status.warming',      dotClass: 'warming-dot',      textClass: 'green--text text--darken-1' },
        live:         { labelKey: 'social.live.status.live',         dotClass: 'live-dot',         textClass: 'red--text text--darken-2' },
        eligibility:  { labelKey: 'social.live.status.eligibility',  dotClass: 'eligibility-dot',  textClass: 'orange--text text--darken-2' },
        error:        { labelKey: 'social.live.status.error',        dotClass: 'error-dot',        textClass: 'red--text text--darken-3' },
      },
      warningMessage: '',
      warningTimer: null,
      sessionStreamCache: {},
      editingStreamChannel: null,
      isStreamModalOpen: false,
      streamFormLoading: false,
      streamFormError: null,
      streamForm: {
        title: '',
        description: '',
        privacy: 'public',
        madeForKids: false,
      },
    }
  },
  computed: {
    ...mapState('naviList', ['allowInitialized', 'allowList']),
    canUseSocialLive() {
      const initialized = this.allowInitialized;
      const rawAllow = this.allowList && Object.prototype.hasOwnProperty.call(this.allowList, 'social_live')
        ? this.allowList.social_live
        : undefined;
      const normalized = this.normalizePermissionFlag(rawAllow);

      if (!initialized) {
        return true;
      }

      return normalized !== false;
    },
    isPrimaryLiveActive() {
      const info = this.liveInfo || {};
      const rawStatus =
        info?.live_status ??
        info?.liveStatus ??
        info?.live_state ??
        info?.liveState ??
        info?.status ??
        info?.state ??
        info?.stream_status ??
        info?.streamStatus ??
        null;
      const normalized = this.normalizeLiveStateFlag(rawStatus);
      return normalized === undefined ? true : normalized;
    },
    dialogTitle() {
      if (this.modalStep === 'youtubeForm') {
        return this.translate('social.live.stream_modal.title', 'Live stream information');
      }
      if (this.modalStep === 'completed') {
        return this.translate('social.integration.add_channel', 'Add channel');
      }
      return this.translate('social.integration.add_channel', 'Add channel');
    },
    privacyItems() {
      void this.$store.getters.currentLanguage;
      const translate = key => (this.$store.getters.t ? this.$store.getters.t(key) : key);
      return [
        {
          label: translate('social.upload.visibility.public'),
          value: 'public',
          desc: translate('social.upload.visibility.public_desc'),
        },
        {
          label: translate('social.upload.visibility.private'),
          value: 'private',
          desc: translate('social.upload.visibility.private_desc'),
        },
        {
          label: translate('social.upload.visibility.unlisted'),
          value: 'unlisted',
          desc: translate('social.upload.visibility.unlisted_desc'),
        },
      ];
    },
  },
  watch: {
    context(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.resetLiveSessionState();
      }
      this.loadChannels();
      this.clearInlineWarning();
    },
    liveInfo: {
      handler(newVal, oldVal) {
        const nextUuid = newVal && newVal.uuid ? newVal.uuid : null;
        const prevUuid = oldVal && oldVal.uuid ? oldVal.uuid : null;
        if (nextUuid !== prevUuid) {
          this.resetLiveSessionState();
          this.loadChannels();
          this.clearInlineWarning();
        }
      },
      deep: false,
    },
    sourceType(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.resetLiveSessionState();
        this.loadChannels();
        this.clearInlineWarning();
      }
    },
    canUseSocialLive(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.loadChannels();
      } else if (!newVal && oldVal) {
        this.clearChannelState();
      }
    },
  },
  beforeDestroy() {
    this.clearInlineWarning();
  },
  methods: {
    clearInlineWarning() {
      if (this.warningTimer) {
        clearTimeout(this.warningTimer);
        this.warningTimer = null;
      }
      this.warningMessage = '';
    },
    showInlineWarning(message) {
      this.clearInlineWarning();
      this.warningMessage = message;
      this.warningTimer = setTimeout(() => {
        this.warningMessage = '';
        this.warningTimer = null;
      }, 4000);
    },
    goToSocialSettings() {
      if (this.$router && typeof this.$router.push === 'function') {
        this.$router.push('/settings/social');
      } else if (typeof window !== 'undefined') {
        window.location.href = '/settings/social';
      }
    },
    openStreamModal(channel) {
      if (!channel) return;
      this.editingStreamChannel = channel;
      this.streamFormLoading = true;
      this.streamFormError = null;
      this.isStreamModalOpen = true;
      this.loadStreamForm(channel);
    },
    closeStreamModal() {
      this.resetStreamModal();
    },
    async loadStreamForm(channel) {
      const cached = this.sessionStreamCache[channel.id]?.settings;
      const defaults = cached || this.getDefaultLiveSettings();
      this.streamForm = { ...defaults };

      const sessionId = this.getChannelSessionId(channel);
      if (!sessionId) {
        this.streamFormLoading = false;
        return;
      }

      try {
        const response = await SocialApi.fetchSessionStream(sessionId, channel.id);
        const normalized = this.normalizeLiveSessionSettings(response);
        const synced = Boolean(response?.updated_at);
        this.setStreamCache(channel.id, sessionId, normalized, synced);
        this.streamForm = { ...normalized };
        if (!channel.sessionId) {
          this.updateChannelSession(channel, { id: sessionId });
        }
      } catch (error) {
        console.warn('[SocialLiveStreamingCard] failed to load stream settings', error);
        this.streamFormError = this.translate('social.live.error.stream_settings_load', 'Could not load stream settings.');
      } finally {
        this.streamFormLoading = false;
      }
    },
    async saveStreamForm() {
      if (!this.editingStreamChannel) return;
      const channel = this.editingStreamChannel;
      const config = { ...this.streamForm };

      this.streamFormLoading = true;
      this.streamFormError = null;
      try {
        const normalized = await this.persistStreamConfig(channel, config, { ensureSession: true });
        if (normalized) {
          this.streamForm = { ...normalized };
          this.showSnackbar(this.translate('social.live.message.stream_saved', 'Stream settings saved.'));
        }
        this.resetStreamModal();
      } catch (error) {
        console.error('[SocialLiveStreamingCard] failed to save stream settings', error);
        this.streamFormError = this.translate('social.live.error.stream_save_failed', 'Failed to save stream settings.');
      } finally {
        this.streamFormLoading = false;
      }
    },
    resetLiveSessionState() {
      this.currentSessionId = null;
      this.sessionDefaultSettings = null;
      this.clearSessionStreamCache();
      this.resetStreamModal();
    },
    applyLiveSession(session) {
      if (!session || !session.id) return;
      const rawSettings =
        (session.options && session.options.live_settings) ||
        session.live_settings ||
        {};
      this.currentSessionId = session.id;
      this.sessionDefaultSettings = this.normalizeLiveSessionSettings(rawSettings);
    },
    normalizeLiveSessionSettings(raw) {
      const defaults = this.getDefaultLiveSettings();
      if (!raw || typeof raw !== 'object') {
        return { ...defaults };
      }
      return {
        title: raw.title || defaults.title,
        description: raw.description || defaults.description,
        privacy: raw.privacy || defaults.privacy,
        madeForKids: typeof raw.made_for_kids === 'boolean' ? raw.made_for_kids : defaults.madeForKids,
      };
    },
    getDefaultLiveSettings() {
      return {
        title: this.resolveDefaultTitle(),
        description: '',
        privacy: 'public',
        madeForKids: false,
      };
    },
    getChannelSessionId(channel) {
      if (!channel) return null;
      return channel.sessionId || channel?.meta?.session?.id || this.currentSessionId || null;
    },
    getStreamSettingsForChannel(channel) {
      if (!channel) return this.getDefaultLiveSettings();
      const cached = this.sessionStreamCache?.[channel.id]?.settings;
      if (cached) return cached;
      if (this.sessionDefaultSettings) return this.sessionDefaultSettings;
      return this.buildLiveConfigDefaults(channel?.meta?.integration);
    },
    async ensureStreamSettings(channel, options = {}) {
      const {
        ensureSession = false,
        persistDefaults = false,
        fallbackConfig = null,
      } = options;

      let sessionId = this.getChannelSessionId(channel);
      const baseConfig = fallbackConfig || this.getStreamSettingsForChannel(channel);

      if (!sessionId && ensureSession) {
        sessionId = await this.ensureSessionForChannel(channel, baseConfig);
      }
      if (!sessionId) {
        return baseConfig;
      }

      const cached = this.sessionStreamCache[channel.id];
      if (cached && cached.sessionId === sessionId) {
        if (persistDefaults && !cached.synced) {
          await this.persistStreamConfig(channel, cached.settings, {
            ensureSession: false,
            sessionId,
            silent: true,
          });
        }
        return cached.settings;
      }

      try {
        const response = await SocialApi.fetchSessionStream(sessionId, channel.id);
        const normalized = this.normalizeLiveSessionSettings(response);
        const synced = Boolean(response?.updated_at);
        this.setStreamCache(channel.id, sessionId, normalized, synced);

        if (persistDefaults && !synced) {
          await this.persistStreamConfig(channel, normalized, {
            ensureSession: false,
            sessionId,
            silent: true,
          });
        }

        return normalized;
      } catch (error) {
        console.warn('[SocialLiveStreamingCard] ensureStreamSettings failed', error);
        return baseConfig;
      }
    },
    async ensureSessionForChannel(channel, config) {
      const existing = this.getChannelSessionId(channel);
      if (existing) {
        return existing;
      }
      const payload = this.buildSessionCreationPayload(channel, config);
      try {
        const session = await SocialApi.createLiveSession(payload);
        if (session?.id) {
          this.updateChannelSession(channel, session);
          return session.id;
        }
      } catch (error) {
        console.error('[SocialLiveStreamingCard] Failed to create live session', error);
      }
      return null;
    },
    buildSessionCreationPayload(channel, config) {
      const info = this.liveInfo || {};
      const liveUuid = this.getLiveUuid();
      const sourceType = this.resolveSourceType();
      const contextKey = this.resolveContextKey();
      const title = config.title || this.resolveDefaultTitle();
      const description = config.description || '';

      const options = this.compactObject({
        context: 'live_relay',
        context_key: contextKey,
        live_uuid: liveUuid,
        stream_name: info.stream_name || info.streamName || null,
        stream_name_by_user: info.stream_name_by_user || info.streamNameByUser || null,
        play_url: info.play_url || info.playUrl || info.HLS || null,
        publish_url: info.publish_url || info.publishUrl || null,
        source_type: sourceType,
        app_type: info.appType || info.app_type || null,
        application: info.application || null,
        live_settings: this.serializeLiveSettings(config),
      });

      return {
        title,
        description,
        targets: [channel.id],
        status: 'draft',
        options,
        stream_options: {
          [channel.id]: this.compactObject({
            privacy: config.privacy,
            made_for_kids: config.madeForKids,
          }),
        },
      };
    },
    updateChannelSession(channel, session) {
      if (!channel || !session) return;
      channel.sessionId = session.id;
      if (channel.meta && typeof channel.meta === 'object') {
        channel.meta.session = session;
      }
      const idx = this.channels.findIndex(item => item.id === channel.id);
      if (idx !== -1) {
        this.$set(this.channels[idx], 'sessionId', session.id);
        if (this.channels[idx].meta) {
          this.$set(this.channels[idx].meta, 'session', session);
        }
      }
    },
    compactObject(obj) {
      if (!obj || typeof obj !== 'object') {
        return {};
      }
      return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => value !== undefined && value !== null)
      );
    },
    ensureLiveSessionSeed(channel) {
      if (this.sessionDefaultSettings) {
        return this.sessionDefaultSettings;
      }
      const integration = channel?.meta?.integration;
      const seeded = this.buildLiveConfigDefaults(integration);
      this.sessionDefaultSettings = seeded;
      return seeded;
    },
    getEffectiveLiveSettings(channel) {
      const cached = this.sessionStreamCache?.[channel?.id]?.settings;
      if (cached) {
        return cached;
      }
      if (this.sessionDefaultSettings) {
        return this.sessionDefaultSettings;
      }
      return this.buildLiveConfigDefaults(channel?.meta?.integration);
    },
    setStreamCache(channelId, sessionId, settings, synced = false) {
      if (!channelId) return;
      const safeSettings = {
        ...this.getDefaultLiveSettings(),
        ...(settings || {}),
      };
      this.$set(this.sessionStreamCache, channelId, {
        sessionId,
        settings: safeSettings,
        synced: Boolean(synced),
      });
    },
    async persistStreamConfig(channel, config, options = {}) {
      const {
        ensureSession = true,
        sessionId: forcedSessionId = null,
        silent = false,
      } = options;
      let sessionId = forcedSessionId ?? this.getChannelSessionId(channel);

      if (!sessionId && ensureSession) {
        sessionId = await this.ensureSessionForChannel(channel, config);
      }
      if (!sessionId) {
        if (!silent) {
          this.showSnackbar(this.translate('social.live.message.session_create_failed', 'Failed to create live session.'), true);
        }
        return null;
      }

      try {
        const payload = this.serializeLiveSettings(config);
        const response = await SocialApi.saveSessionStream(sessionId, channel.id, payload);
        const normalized = this.normalizeLiveSessionSettings(response);
        this.updateChannelSession(channel, { id: sessionId });
        this.setStreamCache(channel.id, sessionId, normalized, true);
        return normalized;
      } catch (error) {
        if (!silent) {
          throw error;
        }
        console.warn('[SocialLiveStreamingCard] persistStreamConfig failed silently', error);
        return null;
      }
    },
    buildLiveConfigDefaults(integration) {
      const settings = (integration && integration.settings && typeof integration.settings === 'object')
        ? integration.settings
        : {};
      const fallbackTitle = this.resolveDefaultTitle();
      return {
        title: settings.live_title || fallbackTitle,
        description: settings.live_description || '',
        privacy: settings.live_privacy || 'public',
        madeForKids: typeof settings.live_made_for_kids === 'boolean' ? settings.live_made_for_kids : false,
      };
    },
    resolveDefaultTitle() {
      const info = this.liveInfo || {};
      return info.stream_name_by_user || info.stream_name || this.defaultTitle || 'StreamOn Live';
    },
    normalizePermissionFlag(value) {
      if (value === null || value === undefined || value === '') {
        return undefined;
      }
      if (typeof value === 'boolean') {
        return value;
      }
      if (typeof value === 'number') {
        return value !== 0;
      }
      const normalized = String(value).trim().toLowerCase();
      if (!normalized.length) {
        return undefined;
      }
      if (truthyAllowValues.has(normalized)) {
        return true;
      }
      if (falsyAllowValues.has(normalized)) {
        return false;
      }
      const numeric = Number(normalized);
      if (!Number.isNaN(numeric)) {
        return Boolean(numeric);
      }
      return undefined;
    },
    normalizeLiveStateFlag(value) {
      if (value === null || value === undefined || value === '') {
        return undefined;
      }
      if (typeof value === 'boolean') {
        return value;
      }
      if (typeof value === 'number') {
        return value !== 0;
      }
      const normalized = String(value).trim().toLowerCase();
      if (!normalized.length) {
        return undefined;
      }
      if (truthyLiveStatusValues.has(normalized)) {
        return true;
      }
      if (falsyLiveStatusValues.has(normalized)) {
        return false;
      }
      const numeric = Number(normalized);
      if (!Number.isNaN(numeric)) {
        return Boolean(numeric);
      }
      return undefined;
    },
    clearChannelState() {
      this.channels = [];
      this.loadError = null;
      this.loading = false;
      this.resetLiveSessionState();
      this.clearInlineWarning();
    },
    clearSessionStreamCache() {
      this.sessionStreamCache = {};
    },
    resetStreamModal() {
      this.editingStreamChannel = null;
      this.streamFormLoading = false;
      this.streamFormError = null;
      this.streamForm = this.getDefaultLiveSettings();
      this.isStreamModalOpen = false;
    },
    isPlatformDisabled(opt) {
      const val = (opt && opt.value) ? String(opt.value).toLowerCase() : '';
      return val === 'instagram';
    },
    async loadChannels() {
      if (!this.canUseSocialLive) {
        this.clearChannelState();
        return;
      }
      this.loading = true;
      this.loadError = null;
      try {
        const [integrations, liveSessions] = await Promise.all([
          SocialApi.fetchIntegrations({ include_inactive: true }),
          SocialApi.fetchLiveSessions(),
        ]);
        const sessionIndex = this.indexSessionsByIntegration(liveSessions);
        this.channels = (Array.isArray(integrations) ? integrations : [])
          .map(intg => this.composeChannel(intg, sessionIndex))
          .filter(Boolean);
      } catch (error) {
        console.error('[SocialLiveStreamingCard] failed to load channels', error);
        const fallback = this.translate('social.live.card.load_error', 'Failed to load social channels.');
        this.loadError = error?.response?.data?.message || fallback;
        this.channels = [];
      } finally {
        this.loading = false;
      }
    },
    indexSessionsByIntegration(sessions) {
      const map = {};
      (Array.isArray(sessions) ? sessions : []).forEach(session => {
        const targets = Array.isArray(session?.targets) ? session.targets : [];
        targets.forEach(target => {
          const integrationId = target?.integration?.id;
          if (!integrationId) return;
          if (!map[integrationId]) {
            map[integrationId] = [];
          }
          map[integrationId].push({
            session,
            target,
            contextKey: session?.context_key || session?.context || session?.options?.context_key || null,
          });
        });
      });
      return map;
    },
    composeChannel(integration, sessionIndex) {
      if (!integration) return null;
      const platformKey = (integration.platform?.key || integration.platform?.name || '').toLowerCase();
      const settings = (integration.settings && typeof integration.settings === 'object') ? integration.settings : {};
      const credentials = (integration.credentials && typeof integration.credentials === 'object') ? integration.credentials : {};
      const desiredContext = this.resolveContextKey();
      const relatedSessions = sessionIndex?.[integration.id] || [];
      let matchedEntry = null;

      if (desiredContext) {
        matchedEntry = relatedSessions.find(entry => entry.contextKey === desiredContext);
      }
      if (!matchedEntry && !desiredContext && relatedSessions.length) {
        matchedEntry = relatedSessions[0];
      }

      const session = matchedEntry?.session || null;
      const target = matchedEntry?.target || null;
      const status = this.normalizeStatus(target?.status, session?.status, integration.status);
      if (session) {
        this.applyLiveSession(session);
      }
      return {
        id: integration.id,
        platform: platformKey || 'unknown',
        platformKey: platformKey || 'unknown',
        connected: integration.status === 'active' && !!integration.has_refresh_token,
        needReconnect: integration.status !== 'active' || !integration.has_refresh_token,
        name: integration.external_name || settings.title || integration.platform?.name || integration.platform?.key || 'Channel',
        channelUrl:
          integration.channel_url ||
          settings.channel_url ||
          credentials.channel_url ||
          '',
        account: settings.username || credentials.email || integration.external_id || '',
        status,
        thumb: settings.thumbnail_url || integration.thumbnail_url || '',
        sessionId: session?.id || null,
        liveVideoUrl: this.resolveLiveVideoUrl(platformKey, target),
        meta: {
          integration,
          session,
          target,
          contextKey: matchedEntry?.contextKey || null,
        },
      };
    },
    normalizeStatus(targetStatus, sessionStatus, integrationStatus) {
      const targetKey = (targetStatus || '').toLowerCase();
      if (targetKey === 'streaming') return 'live';
      if (targetKey === 'pending' || targetKey === 'starting') return 'warming';
      if (targetKey === 'eligibility_required') return 'eligibility';
      if (targetKey === 'failed') return 'error';

      const sessionKey = (sessionStatus || '').toLowerCase();
      if (sessionKey === 'live') return 'live';
      if (sessionKey === 'starting' || sessionKey === 'scheduled') return 'warming';
      if (sessionKey === 'eligibility_required') return 'eligibility';
      if (sessionKey === 'failed' || sessionKey === 'error') return 'error';

      if (integrationStatus !== 'active') return 'error';
      return 'idle';
    },
    statusMeta(status) {
      const meta = this.statusMap[status] || this.statusMap.idle;
      const label = meta.labelKey
        ? this.translate(meta.labelKey, meta.labelKey)
        : meta.label;
      return { ...meta, label };
    },
    resolveThumb(ch) {
      if (!ch) return this.defaultThumb;
      const src = ch.thumb;
      if (src && typeof src === 'string') {
        if (src.startsWith('http') || src.startsWith('data:')) {
          return src;
        }
        if (this.thumbsMap[src]) {
          return this.thumbsMap[src];
        }
      }
      return this.defaultThumb;
    },
    resolveContextKey() {
      if (typeof this.context === 'string' && this.context.length) {
        return this.context;
      }
      const info = this.liveInfo || {};
      if (info?.uuid) {
        return `live:${info.uuid}`;
      }
      if (info?.stream_uuid) {
        return `live:${info.stream_uuid}`;
      }
      return null;
    },
    resolveSourceType() {
      const direct = (this.sourceType || '').toLowerCase();
      if (direct === 'wowza' || direct === 'webrtc') {
        return direct;
      }
      const info = this.liveInfo || {};
      const infoType = (info?.source_type || info?.type || info?.appType || '').toLowerCase();
      if (infoType === 'wowza' || infoType === 'webrtc') {
        return infoType;
      }
      return 'webrtc';
    },
    getLiveUuid() {
      const info = this.liveInfo || {};
      return info?.uuid || info?.stream_uuid || info?.streamName || info?.stream_name || null;
    },
    canControlChannel(ch) {
      return !!(ch?.connected && this.getLiveUuid());
    },
    isChannelLive(ch) {
      return (ch?.status || '').toLowerCase() === 'live';
    },
    isChannelBusy(channelId) {
      return !!this.actionLoading[channelId];
    },
    setChannelBusy(channelId, state) {
      this.$set(this.actionLoading, channelId, state);
    },
    async onToggleStream(ch) {
      if (!this.canControlChannel(ch)) {
        const message = this.translate('social.live.message.loading_live_info', 'Loading live information. Please try again shortly.');
        this.showSnackbar(message, true);
        return;
      }

      const isStarting = !this.isChannelLive(ch);
      if (isStarting && !this.isPrimaryLiveActive) {
        const message = this.translate(
          'social.live.message.start_main_live_first',
          'Please start the main live stream before relaying to social media.'
        );
        this.showSnackbar(message, true);
        this.showInlineWarning(message);
        return;
      }

      if (isStarting) {
        await this.ensureStreamSettings(ch, { ensureSession: true, persistDefaults: true });
      }

      await this.runChannelAction(ch.id, async () => {
        if (this.isChannelLive(ch)) {
          await this.stopRelay(ch);
          this.showSnackbar(this.translate('social.live.message.stop_success', 'Stopped the YouTube relay.'));
        } else {
          const session = await this.startRelay(ch);
          if (session?.status === 'eligibility_required') {
            this.handleEligibilityRequirement(session);
          } else {
            this.showSnackbar(this.translate('social.live.message.start_success', 'Started the YouTube relay.'));
          }
        }
        await this.loadChannels();
      });
    },
    async startRelay(ch) {
      const payload = this.buildStartPayload(ch);
      return await SocialApi.startLiveRelay(payload);
    },
    async stopRelay(ch) {
      const payload = this.buildStopPayload(ch);
      await SocialApi.stopLiveRelay(payload);
    },
    buildStartPayload(ch) {
      const liveUuid = this.getLiveUuid();
      if (!liveUuid) {
        throw new Error(this.translate('social.live.error.missing_live_info', 'Live information could not be found.'));
      }
      const info = this.liveInfo || {};
      const contextKey = this.resolveContextKey();
      const integration = ch?.meta?.integration || {};
      const integrationSettings = (integration.settings && typeof integration.settings === 'object') ? integration.settings : {};
      const sourceType = this.resolveSourceType();
      const liveConfig = this.getStreamSettingsForChannel(ch);
      const fallbackTitle = liveConfig?.title
        || info.stream_name_by_user
        || info.stream_name
        || integrationSettings.title
        || this.defaultTitle
        || ch.name
        || 'StreamOn Live';

      return {
        integration_id: ch.id,
        live_uuid: liveUuid,
        title: liveConfig?.title || fallbackTitle,
        description: liveConfig?.description || info.description || '',
        privacy: liveConfig?.privacy || integrationSettings.live_privacy || null,
        latency_preference: integrationSettings.latency_preference || null,
        stream_format: integrationSettings.stream_format || null,
        stream_name: info.stream_name || info.streamName || null,
        stream_name_by_user: info.stream_name_by_user || info.streamNameByUser || null,
        play_url: info.play_url || info.playUrl || info.HLS || null,
        publish_url: info.publish_url || info.publishUrl || null,
        source_type: sourceType,
        app_type: info.appType || info.app_type || null,
        context_key: contextKey,
        context: 'live_relay',
        made_for_kids: typeof liveConfig?.madeForKids === 'boolean' ? liveConfig.madeForKids : null,
      };
    },
    buildStopPayload(ch) {
      const liveUuid = this.getLiveUuid();
      if (!liveUuid) {
        throw new Error(this.translate('social.live.error.missing_live_info', 'Live information could not be found.'));
      }
      return {
        integration_id: ch.id,
        live_uuid: liveUuid,
        context_key: this.resolveContextKey(),
        reason: 'manual_stop',
      };
    },
    async runChannelAction(channelId, action) {
      if (this.isChannelBusy(channelId)) return;
      this.setChannelBusy(channelId, true);
      try {
        await action();
      } catch (error) {
        this.handleActionError(error);
      } finally {
        this.setChannelBusy(channelId, false);
      }
    },
    handleActionError(error) {
      console.error('[SocialLiveStreamingCard] action failed', error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        this.translate('social.live.error.request', 'An error occurred while processing the request.');
      this.showSnackbar(message, true);
    },
    handleEligibilityRequirement(session) {
      const detailed = session?.error_message
        || this.translate('social.live.error.permission_required', 'You must enable live streaming on YouTube before continuing.');
      const message = this.translate(
        'social.live.error.permission_required',
        'You must enable live streaming on YouTube before continuing. Please enable the feature and try again.'
      );
      this.showSnackbar(message, false);

      if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
        const proceed = window.confirm(
          `${detailed}\n\n${this.translate('social.live.confirm.open_permission_page', 'Open the YouTube permissions page now?')}`
        );
        if (proceed && typeof window.open === 'function') {
          window.open('https://www.youtube.com/features', '_blank', 'noopener');
        }
      }
    },
    showSnackbar(message, isError = false) {
      if (this.$store?.dispatch) {
        this.$store.dispatch('snackbar/showSnackbar', { message, error: isError });
      }
    },
    translate(key, fallback) {
      return this.$store?.getters?.t ? this.$store.getters.t(key) : fallback;
    },
    openChannelModal() {
      this.editingMode = false;
      this.editingChannel = null;
      this.selectedPlatform = null;
      this.modalStep = 'select';
      this.resetForm();
      this.isChannelModalOpen = true;
    },
    onSelectPlatform(value) {
      this.selectedPlatform = value;
      if (value === 'instagram') {
        this.$emit('channel-add', { platform: 'instagram' });
        this.closeChannelDialog(); // ✅ 존재하는 메서드로
      }
    },
    resetForm() {
      this.selectedPlatform = null;
      this.modalStep = 'select';
    },
    async goNextFromSelect() {
      if (this.selectedPlatform !== 'youtube') return;
      this.modalStep = 'youtubeForm'; // ✅ 반응형으로 바뀜
    },
    closeChannelDialog() {            // ✅ 추가
      this.isChannelModalOpen = false;
      this.loadChannels();
      // 커스텀 모달이 value/input로 닫히지 않을 때 대비한 폴백 (옵션)
      // this.$refs.channelDialog?.$emit('input', false);
      // if (typeof this.$refs.channelDialog?.closeDialog === 'function') this.$refs.channelDialog.closeDialog();
    },
    truncateText(value, limit = 35) {
      if (!value || typeof value !== 'string') return '';
      const trimmed = value.trim();
      if (!trimmed) return '';
      return trimmed.length > limit ? `${trimmed.slice(0, limit)}...` : trimmed;
    },
    channelLinkHref(link) {
      if (!link || typeof link !== 'string') return '';
      const trimmed = link.trim();
      if (!trimmed) return '';
      return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    },
    resolveLiveVideoUrl(platformKey, target) {
      const key = (platformKey || '').toLowerCase();
      if (key !== 'youtube' || !target) {
        return '';
      }
      const remoteId = target?.remote_identifier
        || target?.stream_config?.driver_response?.video_id
        || target?.stream_config?.driver_response?.broadcast_id;
      return remoteId ? this.buildYoutubeWatchUrl(remoteId) : '';
    },
    buildYoutubeWatchUrl(videoId) {
      return videoId ? `https://www.youtube.com/watch?v=${videoId}` : '';
    },
    shouldShowLiveUrl(channel) {
      return (
        channel?.platform === 'youtube'
        && channel?.status === 'live'
        && !!channel?.liveVideoUrl
      );
    },
    liveUrlForChannel(channel) {
      return channel?.liveVideoUrl || '';
    },
    serializeLiveSettings(config) {
      return {
        title: config?.title,
        description: config?.description,
        privacy: config?.privacy,
        made_for_kids: typeof config?.madeForKids === 'boolean' ? config.madeForKids : undefined,
      };
    },
  },
}
</script>

<style scoped>
/* 카드 공통 */
.social_card_list{  display: flex; align-items: center; justify-content: space-between; }
.sm-logo{ width:48px;height:48px;border-radius:12px;display:flex;align-items:center;justify-content:center; }
.sm-logo.youtube{ background:#dc2626; }
.sm-logo.insta{ background:linear-gradient(135deg,#8b5cf6,#ec4899); }
.sm-alert-dot{
  position:absolute;top:-6px;right:-6px;width:20px;height:20px;border-radius:9999px;background:#f97316;
  display:flex;align-items:center;justify-content:center;
}
.channel-info-block {
  margin-top: 2px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.channel-info-row {
  font-size: 12px;
  color: #4b5563;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.channel-info-label {
  font-weight: 600;
  color: #111827;
}

.channel-info-link {
  color: #2563eb;
  text-decoration: underline;
  word-break: break-all;
}

.channel-info-placeholder {
  color: #6b7280;
}

/* 상태 점 */
.status-dot{ width:10px;height:10px;border-radius:9999px;background:#9ca3af; }          /* idle */
.warming-dot{ width:10px;height:10px;border-radius:9999px;background:#4fd334; animation:pulse 1.4s infinite; } /* warming */
.live-dot{ width:10px;height:10px;border-radius:9999px;background:#ef4444; animation:blink 1.2s infinite; }     /* live */
.eligibility-dot{ width:10px;height:10px;border-radius:9999px;background:#facc15; animation:pulse 1.6s infinite; } /* eligibility */
.error-dot{ width:10px;height:10px;border-radius:9999px;background:#b91c1c; }            /* error */

@keyframes blink{ 0%,100%{opacity:1} 50%{opacity:.2} }
@keyframes pulse{ 0%,100%{transform:scale(1.2);opacity:.9} 50%{transform:scale(1.25);opacity:.5} }

/* 인스타 송출 버튼 */
.sm-gradient-btn{ background:linear-gradient(90deg,#8b5cf6,#ec4899) !important;color:#fff !important; }

/* 모달 플랫폼 선택 */
.platform-selection{ display: flex; gap: 8px; }
.platform-item{
  width: 100%;padding:16px; border-color:#e5e7eb !important; transition:.15s border-color ease,.15s box-shadow ease,.15s background-color ease; cursor:pointer;
}
.platform-item:hover{ background:#f3f9ff;}
.platform-item.is-selected{ border-color:#006cea !important; background:#f3f9ff; }
.platform-item.is-disabled{ cursor:not-allowed; opacity:.6; position:relative; }
.chip_posi { position: absolute; top: 8px; left: 8px; padding: 6px; }
.platform-icon{ width:48px; height:48px; border-radius:12px; display:flex; align-items:center; justify-content:center; }
.platform-icon.youtube{ background:#dc2626; }
.platform-icon.insta{ background:linear-gradient(135deg,#8b5cf6,#ec4899); }
.kids-toggle .v-btn {
  flex: 1;
}

@media(min-width:768px){
  .platform-selection{ grid-template-columns:1fr 1fr; grid-column-gap:12px; }
}

.description {
    height: 90px;
}

.sm-avatar-wrap {
  position: relative;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
}

.sm-avatar {
  border-radius: 50%;
  overflow: hidden;
}

.platform-badge {
  position: absolute;
  right: -20px;
  bottom: -6px;
  width: 16px !important;
  height: 16px !important;
  border-radius: 4px;
  padding: 2px;
  background: none;
}

.sm-disconnect-dot {
  position: absolute;
  right: -3px;
  top: 2px;
  width: 8px;
  height: 8px;
  background: #ea580c;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.06);
}

.empty-dashed { border: 1px solid #ddd; }
.card_title span{ font-size: 14px; color: #777; }
.social-live-locked {
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  background: #f9fafb;
  color: #4b5563;
}
</style>
